import React, {useEffect, useRef, useState} from 'react';
import {useQuery} from '@apollo/client';
import {ArrowLeftOutlined, CameraOutlined} from '@ant-design/icons';
import {Button, Col, Drawer, Empty, Image, Input, Row, Select, Spin, Tag, message} from 'antd';
import {useTranslation} from 'react-i18next';
import {HasResource} from '../../../../components/HasResource';
import {ResourceCode} from '../../../../constants/resourceCodes';
import {normalizeLang, pickDisplayText} from '../../../../utils/i18nText';
import {getPickupFailureReasonText} from '../../../../enums/PickupEnum';
import {
    GET_POP_REVIEW_DETAIL_BY_ID,
    GetPopReviewDetailByIdResponse
} from '../graphql/queries/getPopReviewDetailById';
import {
    PopDisqualifyReasonOption,
    PopReviewAction,
    PopReviewStatus,
    isPopReviewFinished
} from '../graphql/types/PopReviewType';
import {usePopReviewActions} from '../hooks/usePopReviewActions';
import type {ReasonOptionGroup} from '../hooks/usePopReviewOptions';

const {TextArea} = Input;

/** 审核备注长度上限，与 podReview 保持一致 */
const REMARK_MAX_LENGTH = 120;

/**
 * 审核按钮公共尺寸：宽度自适应而非写死 —— 中英西三语文案长度差异大，
 * 固定宽度会让长语种文本溢出按钮边框，故允许折行并跟着长高。
 */
const REVIEW_BUTTON_BASE = 'h-auto min-h-10 py-1.5 w-full whitespace-normal leading-tight';

/**
 * 已审单的结论按钮样式：命中当前结论的一侧加粗边框高亮，另一侧置灰。
 *
 * 对齐 podReview 的「按钮高亮表达结论」，使详情页一眼可见通过还是驳回；
 * 但 POP 一单只审一次（quality 侧审核序号恒为 1，不支持改判），故按钮恒为禁用态。
 * 「已取消」既非通过也非驳回，两侧都不高亮。
 */
const finishedButtonClass = (highlighted: boolean, isApprove: boolean): string => {
    if (!highlighted) {
        return `${REVIEW_BUTTON_BASE} !border-gray-200 !bg-gray-100 !text-gray-400`;
    }
    return isApprove
        ? `${REVIEW_BUTTON_BASE} !border-2 !border-green-500 !bg-green-50 !text-green-600`
        : `${REVIEW_BUTTON_BASE} !border-2 !border-red-500 !bg-red-50 !text-red-500`;
};

/** 栏内的「标签：值」行；标签列定宽以保证同栏各行的值左边缘对齐 */
const LabeledText: React.FC<{label: string; value?: React.ReactNode}> = ({label, value}) => (
    <div className="flex text-sm leading-6 mb-2">
        {/* 标签左对齐 + 固定列宽：值的左边缘仍落在同一条线上 */}
        <span className="text-gray-500 flex-shrink-0 w-[104px] mr-3">{label}:</span>
        <span className="flex-1 min-w-0 break-words">
            {value === null || value === undefined || value === '' ? '—' : value}
        </span>
    </div>
);

/** 地址 + 邮编拼接；两者皆空返回 undefined 交由 LabeledText 兜底 */
const joinAddress = (address?: string, zipcode?: string): string | undefined =>
    [address, zipcode].filter(Boolean).join(' ') || undefined;

/** 联系人姓名 + 电话拼接为「Vivian (+1 9498876992)」；缺一项则只显示另一项 */
const joinContact = (name?: string, phone?: string): string | undefined => {
    if (name && phone) return `${name} (${phone})`;
    return name || phone || undefined;
};

interface PopReviewDetailDrawerProps {
    open: boolean;
    onClose: () => void;
    /**
     * 待审核单 ID 列表。
     * 长度 > 1 即批量模式：逐条展示、审完自动跳下一条（对齐 POD，不是一次提交多条）。
     */
    reviewIds: string[];
    /** 单条审核成功后回调，供列表刷新 */
    onReviewed?: () => void;
    /** 不合格原因下拉选项；按分类分组展示，但仍是扁平多选（选中的是叶子 code） */
    reasonOptions: ReasonOptionGroup[];
    /** 原因码 → 当前语言文案，用于回显已选原因 */
    reasonTextByCode: Record<string, string>;
    /** 原因字典原始数据；查看场景据此按分类分组回显 */
    disqualifyReasons: PopDisqualifyReasonOption[];
    hubNameById: Record<string, string>;
    /** 站点 ID → 名称；未命中回落显示 ID */
    pickupSiteNameById: Record<string, string>;
    /** 司机 account ID → 姓名；未命中回落显示 ID */
    pickupDriverNameById: Record<string, string>;
}

/**
 * 分类配色：选中项挤在输入框里时纯文字 tag 分不出类别，用颜色区分比另起一块列表更紧凑。
 * 按严重度递减：信息虚假(红) > 信息不全(橙) > 信息不完整(蓝) > 其他(灰)。
 * 「信息虚假」用红色 —— 它是唯一指向主观造假的判定，需要更强的视觉权重。
 */
const CATEGORY_TAG_COLOR: Record<string, string> = {
    FALSE_INFO: 'red',
    INCOMPLETE_INFO: 'orange',
    INCOMPLETE_CONTENT: 'blue',
    OTHER: 'default'
};

/** 强化分类层级：默认 optgroup 标题与选项区分很弱，多选时易看成一片扁平列表 */
const REASON_DROPDOWN_CLASS = [
    '[&_.ant-select-item-group]:text-gray-500',
    '[&_.ant-select-item-group]:font-medium',
    '[&_.ant-select-item-group]:bg-gray-50',
    '[&_.ant-select-item-group]:mt-1',
    'first:[&_.ant-select-item-group]:mt-0',
    '[&_.ant-select-item-option]:pl-6'
].join(' ');

interface ReasonGroup {
    categoryCode: string;
    categoryLabel: string;
    reasons: {code: string; label: string}[];
}

/** 按分类分组展示不合格原因；仅用于查看场景，编辑场景由下拉自身承载 */
const GroupedReasons: React.FC<{groups: ReasonGroup[]}> = ({groups}) => (
    <>
        {groups.map(group => (
            <div key={group.categoryCode} className="mb-1.5 last:mb-0">
                {/* 分类名作为小标题，使三类一眼可辨（与下拉分组同一套文案） */}
                <div className="text-xs text-gray-400">{group.categoryLabel}</div>
                <div className="flex flex-wrap gap-1 mt-0.5">
                    {group.reasons.map(r => (
                        <Tag key={r.code} color={CATEGORY_TAG_COLOR[group.categoryCode]}>
                            {r.label}
                        </Tag>
                    ))}
                </div>
            </div>
        ))}
    </>
);

const PopReviewDetailDrawer: React.FC<PopReviewDetailDrawerProps> = ({
    open,
    onClose,
    reviewIds,
    onReviewed,
    reasonOptions,
    reasonTextByCode,
    disqualifyReasons,
    hubNameById,
    pickupSiteNameById,
    pickupDriverNameById
}) => {
    const {t, i18n} = useTranslation();
    const {handleReview, loading: submitting} = usePopReviewActions();

    const [internalCurrentIndex, setInternalCurrentIndex] = useState(0);
    const [selectedReasonCodes, setSelectedReasonCodes] = useState<string[]>([]);
    const [reviewRemark, setReviewRemark] = useState('');
    // 批量审到最后一条时置位：不自动关抽屉，改为展示「没有更多了」等用户手动关（对齐 POD）
    const [showNoMore, setShowNoMore] = useState(false);
    const prevReviewIdsRef = useRef<string[]>([]);

    const isBatchMode = reviewIds.length > 1;
    const queryId = reviewIds[internalCurrentIndex];

    // 传入列表变化时重置索引与表单，避免沿用上一批的进度
    useEffect(() => {
        const idsChanged =
            prevReviewIdsRef.current.length !== reviewIds.length ||
            !prevReviewIdsRef.current.every((id, index) => id === reviewIds[index]);
        if (idsChanged) {
            prevReviewIdsRef.current = reviewIds;
            setInternalCurrentIndex(0);
        }
    }, [reviewIds]);

    // 切换到下一条时清空上一条的审核输入
    useEffect(() => {
        setSelectedReasonCodes([]);
        setReviewRemark('');
    }, [queryId]);

    // 批量模式下停留在最后一条时展示「没有更多了」；单条模式不涉及
    useEffect(() => {
        setShowNoMore(isBatchMode && internalCurrentIndex + 1 === reviewIds.length);
    }, [isBatchMode, internalCurrentIndex, reviewIds.length]);

    // 关闭后重置，避免下次打开残留上一批的「没有更多了」态
    useEffect(() => {
        if (!open) {
            setShowNoMore(false);
        }
    }, [open]);

    const {data, loading, refetch} = useQuery<GetPopReviewDetailByIdResponse>(
        GET_POP_REVIEW_DETAIL_BY_ID,
        {
            variables: {reviewTaskId: queryId},
            skip: !open || !queryId,
            fetchPolicy: 'network-only'
        }
    );

    const detail = data?.getPopReviewDetailById;
    const item = detail?.item;
    const finished = isPopReviewFinished(item?.status);
    // 已审单用于高亮结论按钮；「已取消」两者皆 false，两侧按钮均置灰
    const approved = item?.status === PopReviewStatus.APPROVED;
    const rejected = item?.status === PopReviewStatus.REJECTED;

    /**
     * 已选不合格原因按分类分组：平铺会丢掉分类信息，而「信息不全」与「信息虚假」
     * 是不同性质的判定，必须能一眼区分。
     * 顺序沿用服务端字典，不在前端另行排序，避免两处规则漂移。
     */
    /** code → 所属分类，供选中 tag 着色（下拉本身已分组，这里补的是「选中后」的可辨识度） */
    const categoryByCode = React.useMemo(() => {
        const map: Record<string, string> = {};
        disqualifyReasons.forEach(r => {
            if (r.code) {
                map[r.code] = r.categoryCode || '';
            }
        });
        return map;
    }, [disqualifyReasons]);

    const groupedSelectedReasons = React.useMemo(() => {
        const selected = new Set(item?.disqualifyReasonCodes ?? []);
        if (!selected.size) {
            return [];
        }
        const groups: {
            categoryCode: string;
            categoryLabel: string;
            reasons: {code: string; label: string}[];
        }[] = [];
        const indexByCategory = new Map<string, number>();
        const currentLang = normalizeLang(i18n.language);

        disqualifyReasons.forEach(reason => {
            const code = reason.code;
            if (!code || !selected.has(code)) {
                return;
            }
            const categoryCode = reason.categoryCode || '';
            const categoryLabel =
                reason.categoryTexts?.find(e => e.lang === currentLang)?.text ||
                reason.categoryTexts?.find(e => e.lang === 'en')?.text ||
                reason.categoryTexts?.[0]?.text ||
                categoryCode;
            const leaf = {code, label: reasonTextByCode[code] || code};

            const existing = indexByCategory.get(categoryCode);
            if (existing !== undefined) {
                groups[existing].reasons.push(leaf);
                return;
            }
            indexByCategory.set(categoryCode, groups.length);
            groups.push({categoryCode, categoryLabel, reasons: [leaf]});
        });

        // 字典里查不到的码（如原因已下线）仍需展示，归入无分类组，避免结论凭空少一项
        const known = new Set(
            disqualifyReasons.map(r => r.code).filter(Boolean) as string[]
        );
        const orphans = [...selected].filter(c => !known.has(c));
        if (orphans.length) {
            groups.push({
                categoryCode: '__unknown__',
                categoryLabel: t('popReview.detail.uncategorizedReason'),
                reasons: orphans.map(c => ({code: c, label: reasonTextByCode[c] || c}))
            });
        }
        return groups;
    }, [item?.disqualifyReasonCodes, disqualifyReasons, reasonTextByCode, i18n.language, t]);

    const moveToNext = () => {
        if (isBatchMode) {
            const nextIndex = internalCurrentIndex + 1;
            if (nextIndex < reviewIds.length) {
                setInternalCurrentIndex(nextIndex);
                return;
            }
            // 批量审到最后一条：不自动关，停留并展示「没有更多了」（对齐 POD）。
            // 重查当前条使其刷新为已终止只读态，避免刚审完的按钮仍可点。
            void refetch();
            return;
        }
        onClose();
    };

    const submit = async (action: PopReviewAction) => {
        if (!queryId) {
            return;
        }

        if (action === PopReviewAction.APPROVE && selectedReasonCodes.length > 0) {
            message.error(t('popReview.messages.approveWithReasonsConflict'));
            return;
        }
        const ok = await handleReview(
            queryId,
            action,
            action === PopReviewAction.REJECT ? selectedReasonCodes : undefined,
            reviewRemark,
            // 备注语种取前端当前 locale：前端本就持有，省一次服务端语种识别
            normalizeLang(i18n.language)
        );
        if (ok) {
            onReviewed?.();
            moveToNext();
        }
    };


    // 标题即预约单号（对齐 podReview 用运单号做标题）；批量模式下仍需进度提示
    const title = isBatchMode
        ? t('popReview.detail.titleBatch', {
              current: internalCurrentIndex + 1,
              total: reviewIds.length
          })
        : item?.appointmentNumber || '';

    return (
        <Drawer
            title={
                <div className="flex items-center">
                    <ArrowLeftOutlined
                        className="mr-3 cursor-pointer hover:text-gray-600"
                        onClick={onClose}
                    />
                    <span className="text-lg font-medium">{title}</span>
                </div>
            }
            placement="right"
            // 近全屏：照片是审核的核心依据，需要足够版面（与 podReview 一致）
            width="calc(100vw - 80px)"
            onClose={onClose}
            open={open}
            closable={false}
            destroyOnClose
            styles={{body: {padding: 0}, header: {borderBottom: '1px solid #f0f0f0'}}}
        >
            <Spin spinning={loading}>
                {!item ? (
                    <div className="px-8 py-6">
                        <Empty description={t('popReview.detail.notFound')} />
                    </div>
                ) : (
                    <div className="h-full bg-white">
                    {/* 只展示 PRD 列出的字段，避免审核员在无关信息里找判断依据 */}
                    <div className="px-6 py-2">
                        <div className="border border-gray-200 rounded-lg bg-white">
                            {/*
                                预约单号行（PRD 有此行）：标题栏是黑色单号、此处是紫色可辨识的业务主键。
                                两处并存是 PRD 的设计 —— 标题用于抽屉定位，卡片内是单据本身的标识。
                            */}
                            <div className="flex items-center px-6 py-3 border-b border-gray-100">
                                <span className="text-gray-500 text-sm w-[104px] flex-shrink-0">
                                    {t('popReview.detail.appointmentLabel')}
                                </span>
                                <span className="text-purple-600 font-medium">
                                    {item.appointmentNumber || '—'}
                                </span>
                            </div>

                            <Row className="px-6 py-4">
                                {/* ---------- 站点信息 ---------- */}
                                <Col span={8} className="pr-8">
                                    <div className="mb-3 font-medium">
                                        {t('popReview.detail.siteSection')}
                                    </div>
                                    <LabeledText
                                        label={t('popReview.detail.siteId')}
                                        // 优先用揽收侧直接给出的站点名（不依赖是否选过 Hub）；
                                        // 其次用筛选区下拉的 id→name 映射；都没有才回落显示 ID
                                        value={
                                            detail?.pickupInfo?.siteName ||
                                            (item.pickupSiteId
                                                ? pickupSiteNameById[item.pickupSiteId] || item.pickupSiteId
                                                : undefined)
                                        }
                                    />
                                    <LabeledText
                                        label={t('popReview.detail.pickupAddress')}
                                        value={joinAddress(
                                            detail?.pickupInfo?.address,
                                            detail?.pickupInfo?.zipcode
                                        )}
                                    />
                                    <LabeledText
                                        label={t('popReview.detail.contactName')}
                                        value={joinContact(
                                            detail?.pickupInfo?.contactName,
                                            detail?.pickupInfo?.contactPhone
                                        )}
                                    />
                                </Col>

                                {/* ---------- 揽收信息（淡竖线分隔） ---------- */}
                                <Col span={8} className="px-8 border-l border-gray-100">
                                    <div className="mb-3 font-medium">
                                        {t('popReview.detail.pickupSection')}
                                    </div>
                                    <LabeledText
                                        label={t('popReview.detail.pickupFailureReasonCode')}
                                        // 展示原因文案而非码值；未收录的码回落显示码本身
                                        value={
                                            item.pickupFailureReasonCode
                                                ? getPickupFailureReasonText(
                                                      item.pickupFailureReasonCode,
                                                      i18n.language
                                                  )
                                                : undefined
                                        }
                                    />
                                    <LabeledText
                                        label={t('popReview.detail.gps')}
                                        // 缺失定位本身即「疑似虚假」信号，须明确提示而非留空
                                        value={
                                            item.pickupGpsLatitude && item.pickupGpsLongitude
                                                ? `${item.pickupGpsLatitude}, ${item.pickupGpsLongitude}`
                                                : t('popReview.detail.gpsUnavailable')
                                        }
                                    />
                                    <LabeledText
                                        label={t('popReview.table.pickupSiteDistance')}
                                        value={
                                            item.pickupSiteDistanceMeters == null
                                                ? t('popReview.table.distanceUnavailable')
                                                : t('popReview.detail.distanceMeters', {
                                                      value: item.pickupSiteDistanceMeters
                                                  })
                                        }
                                    />
                                </Col>

                                {/* ---------- 审核信息 + 操作（淡竖线分隔） ---------- */}
                                <Col span={8} className="pl-8 border-l border-gray-100">
                                    <div className="mb-3 font-medium">
                                        {t('popReview.detail.reviewSection')}
                                    </div>
                                    <div className="flex gap-4">
                                        <div className="flex-1 min-w-0">
                                            {/* 待审核单尚无审核人，整行不展示（而非显示「—」） */}
                                            {item.reviewerId && (
                                                <LabeledText
                                                    label={t('popReview.detail.reviewer')}
                                                    // 后端 DataLoader 回查 account 得姓名；查不到回落显示 ID
                                                    value={item.reviewer?.name || item.reviewerId}
                                                />
                                            )}
                                            {/* 已终止单只读回显；待审核单给出录入控件 */}
                                            {finished ? (
                                                <>
                                                    <div className="flex text-sm leading-6 mb-2">
                                                        <span className="text-gray-500 flex-shrink-0 w-[104px] mr-3">
                                                            {t('popReview.detail.failureReason')}:
                                                        </span>
                                                        <div className="flex-1 min-w-0">
                                                            {groupedSelectedReasons.length ? (
                                                                <GroupedReasons groups={groupedSelectedReasons} />
                                                            ) : (
                                                                <span>—</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <LabeledText
                                                        label={t('popReview.detail.reviewRemark')}
                                                        // 按当前 locale 取译文，无命中回落原文（译文异步补且允许丢失）
                                                        value={pickDisplayText(
                                                            item.reviewRemark,
                                                            normalizeLang(i18n.language)
                                                        )}
                                                    />
                                                </>
                                            ) : (
                                                <>
                                                    <div className="mb-2">
                                                        <div className="mb-1 text-sm text-gray-500">
                                                            {t('popReview.detail.failureReason')}
                                                        </div>
                                                        <Select
                                                            mode="multiple"
                                                            allowClear
                                                            className="w-full"
                                                            placeholder={t('popReview.detail.reasonPlaceholder')}
                                                            value={selectedReasonCodes}
                                                            onChange={setSelectedReasonCodes}
                                                            options={reasonOptions}
                                                            // 分组标题加重、选项缩进：让「信息不全 / 信息虚假 / 信息不完整」
                                                            // 三层结构在展开时一眼可辨，而非与选项混作一片
                                                            popupClassName={REASON_DROPDOWN_CLASS}
                                                            // 选中后按分类着色 —— 纯文字 tag 挤在一起时分不出类别
                                                            tagRender={({value, closable, onClose, label}) => (
                                                                <Tag
                                                                    color={
                                                                        CATEGORY_TAG_COLOR[
                                                                            categoryByCode[value as string]
                                                                        ]
                                                                    }
                                                                    closable={closable}
                                                                    // 阻止默认行为，否则点关闭会先触发下拉展开
                                                                    onMouseDown={e => e.preventDefault()}
                                                                    onClose={onClose}
                                                                    style={{marginRight: 3}}
                                                                >
                                                                    {label}
                                                                </Tag>
                                                            )}
                                                        />
                                                    </div>
                                                    <div>
                                                        <div className="mb-1 text-sm text-gray-500">
                                                            {t('popReview.detail.reviewRemark')}
                                                        </div>
                                                        <TextArea
                                                            rows={2}
                                                            // 禁止拖拽调整大小：卡片三栏等高，拉伸会撑破布局
                                                            style={{resize: 'none'}}
                                                            // 与 podReview 一致的 120 字上限：备注是审计留档，
                                                            // showCount 让审核员写之前就知道余量，而非提交时才被截断
                                                            maxLength={REMARK_MAX_LENGTH}
                                                            showCount
                                                            value={reviewRemark}
                                                            onChange={e => setReviewRemark(e.target.value)}
                                                            placeholder={
                                                                t('popReview.detail.remarkPlaceholder') as string
                                                            }
                                                        />
                                                    </div>
                                                </>
                                            )}
                                        </div>

                                        <div className="flex-shrink-0 flex flex-col gap-3 min-w-[96px] max-w-[132px]">
                                            {finished ? (
                                                /*
                                                    已审单：按钮保留但禁用，靠高亮标出本单结论（对齐 podReview），
                                                    否则「通过」的单在详情页只剩「失败原因 —」，看不出是已通过还是未审。
                                                    不包 OPERATE 资源码 —— 看结论属于查看权限，不是操作权限。
                                                */
                                                <>
                                                    <Button
                                                        disabled
                                                        className={finishedButtonClass(approved, true)}
                                                    >
                                                        {t('popReview.detail.approve')}
                                                    </Button>
                                                    <Button
                                                        disabled
                                                        className={finishedButtonClass(rejected, false)}
                                                    >
                                                        {t('popReview.detail.reject')}
                                                    </Button>
                                                </>
                                            ) : (
                                                <HasResource resourceCodes={[ResourceCode.cs.CS_POP_REVIEW_OPERATE]}>
                                                    <Button
                                                        loading={submitting}
                                                        onClick={() => submit(PopReviewAction.APPROVE)}
                                                        className={`${REVIEW_BUTTON_BASE} !border-green-300 !text-green-600 !bg-green-50 hover:!border-green-400 hover:!text-green-700`}
                                                    >
                                                        {t('popReview.detail.approve')}
                                                    </Button>
                                                    <Button
                                                        loading={submitting}
                                                        onClick={() => submit(PopReviewAction.REJECT)}
                                                        className={`${REVIEW_BUTTON_BASE} !border-red-200 !text-red-500 !bg-red-50 hover:!border-red-300 hover:!text-red-600`}
                                                    >
                                                        {t('popReview.detail.reject')}
                                                    </Button>
                                                </HasResource>
                                            )}
                                            {/*
                                                批量导航独立于审核按钮 / OPERATE 资源码：
                                                停在最后一条时展示「没有更多了」（含审完最后一条 finished 的情况），
                                                否则展示「跳过」推进到下一条。
                                            */}
                                            {isBatchMode &&
                                                (showNoMore ? (
                                                    <div className="py-1 w-full text-center text-sm text-gray-500">
                                                        {t('popReview.detail.noMore')}
                                                    </div>
                                                ) : (
                                                    <Button
                                                        type="text"
                                                        className="h-auto py-1 w-full whitespace-normal leading-tight text-purple-500"
                                                        onClick={moveToNext}
                                                    >
                                                        {t('popReview.detail.skip')} →
                                                    </Button>
                                                ))}
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    </div>


                    {/* POP 照片展示区域（布局与 podReview 一致） */}
                    <div className="px-8 py-4 bg-gray-50 border-t flex-1 min-h-0">
                        <div
                            className={`grid gap-4 ${
                                (detail?.popPhotoUrls?.length || 0) <= 3
                                    ? 'grid-cols-3 h-[75vh]'
                                    : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 max-h-[75vh] overflow-y-auto'
                            }`}
                        >
                            {detail?.popPhotoUrls && detail.popPhotoUrls.length > 0 ? (
                                <Image.PreviewGroup>
                                    {detail.popPhotoUrls.map((url, index) => (
                                        <div
                                            key={index}
                                            className={`aspect-[9/16] border border-gray-300 rounded-lg overflow-hidden hover:border-purple-400 transition-colors bg-white ${
                                                (detail?.popPhotoUrls?.length || 0) <= 3
                                                    ? 'h-full'
                                                    : 'max-h-[75vh] mx-auto'
                                            }`}
                                        >
                                            <Image
                                                src={url}
                                                alt={`POP ${index + 1}`}
                                                className="object-cover w-full h-full"
                                                preview={{
                                                    mask: (
                                                        <div className="flex items-center justify-center">
                                                            <span className="text-white text-sm font-medium">
                                                                {t('popReview.detail.clickToPreview')}
                                                            </span>
                                                        </div>
                                                    )
                                                }}
                                            />
                                        </div>
                                    ))}
                                </Image.PreviewGroup>
                            ) : (
                                /* 无照片时用相机图标占位（对齐 podReview），保持版面结构稳定 */
                                <>
                                    <div className="aspect-[9/16] h-full border border-gray-300 rounded-lg flex items-center justify-center bg-white">
                                        <CameraOutlined className="text-8xl text-gray-400" />
                                    </div>
                                    <div className="aspect-[9/16] h-full border border-gray-300 rounded-lg flex items-center justify-center bg-white">
                                        <CameraOutlined className="text-8xl text-gray-400" />
                                    </div>
                                    <div className="aspect-[9/16] h-full border border-gray-300 rounded-lg flex items-center justify-center bg-white">
                                        <CameraOutlined className="text-8xl text-gray-400" />
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                    </div>
                )}
            </Spin>
        </Drawer>
    );
};

export default PopReviewDetailDrawer;
